import React, { useState } from 'react'

import { ProductStrategy } from './productStrategy'
import { PromotionConfig } from '@/constants/UiPropertyKeys'
import { eventTracker, trackPurchaseCompleted } from '@/config/trackingFunctions'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { firebaseConfiguration } from '@/config/analytics'
import { getValue } from 'firebase/remote-config'
import { defaultFirebaseConfigurations } from '@/config/defaultFirebaseConfiguration'
import {
  AppShell,
  AppButton,
  AppDisclosure,
  CheckoutShell,
  type AppDisclosureItem,
} from '@/pages/external/components/shell'

// Cache Stripe promise so it's only created once per page lifecycle
let stripePromiseCache: Promise<Stripe | null> | null = null
const getStripePromise = () => {
  if (!stripePromiseCache) {
    let stripeApiKey: string
    try {
      const remoteKey = getValue(firebaseConfiguration, 'stripeApiKey').asString()
      stripeApiKey = remoteKey || defaultFirebaseConfigurations.stripeApiKey
    } catch {
      stripeApiKey = defaultFirebaseConfigurations.stripeApiKey
    }
    stripePromiseCache = loadStripe(stripeApiKey)
  }
  return stripePromiseCache
}

const BASE_FAQ_ITEMS = [
  {
    question: 'What is a DUPR Rating Reset?',
    answer:
      'DUPR Reset is a limited-time, risk-free opportunity to re-evaluate your rating using only matches played during the Reset period. Once registered, simply play in a tournament, league, or club match anywhere in the world that submits scores to DUPR.\n\nComplete a minimum requirement of 8 matches with 2 different partners (for doubles), or 8 matches against 2 different opponents (for singles). All matches except self reported matches will count. If your Reset rating is lower than your original rating, your higher rating is retained. All upside, zero downside.',
  },
  {
    question: 'When is the Reset period?',
    answer:
      'The Reset period runs from March 16, 2026 (or from your sign up date if after 3/16) to May 31, 2026. Final match uploads will be accepted until May 31, 2026 at 9:00 PM (PST).',
  },
  {
    question: 'Can my rating go down if I do a Reset?',
    answer: 'No. You always keep the higher of your original rating or your Reset rating.',
  },
  {
    question: 'If you play more than eight matches, which matches count?',
    answer:
      'All matches played during the Reset period will count. There is no maximum limit on the number of matches, partners, or opponents.',
  },
  {
    question: 'Can you reset both your singles and doubles DUPR?',
    answer:
      'Yes. DUPR Reset applies to the player’s account, not a single rating. To be eligible to reset either rating, you must meet the minimum requirement of 8 matches with 2 different partners (for doubles), or 8 matches against 2 different opponents (for singles).',
  },
  {
    question: 'Will ratings fluctuate during the Reset period after each match, or be frozen at the original rating?',
    answer:
      'Ratings will continue to move normally throughout the entire Reset period. They can go up or down from the original rating based on match results. After the Reset Period ends, a player’s DUPR is set to the higher of their Reset rating or original rating at the start of the Reset Period.',
  },
  {
    question: 'What happens after the Reset ends?',
    answer:
      'You’ll receive a personalized summary showing: 1) your original rating, 2) your Reset rating, 3) number of matches counted, and 4) your final rating going forward.',
  },
]

const EXTENSION_FAQ_ITEMS = BASE_FAQ_ITEMS.map((item) =>
  item.question === 'When is the Reset period?'
    ? {
        question: 'When is the Reset period?',
        answer:
          'The Reset period runs from March 16, 2026 (or from your sign up date if after 3/16) to May 31, 2026. Final match uploads will be accepted until May 31, 2026 at 9:00 PM (PST).',
      }
    : item
)

export type ResetVariant = 'original' | 'extension'

interface ResetProductPageProps {
  productStrategies: ProductStrategy[]
  base64ClientKey?: string
  promotionId?: string
  promotionConfig?: PromotionConfig
  variant?: ResetVariant
}

export const ResetProductPage: React.FC<ResetProductPageProps> = ({
  productStrategies,
  base64ClientKey,
  promotionId,
  promotionConfig,
  variant = 'original',
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)

  const strategy = productStrategies[0]
  const product = strategy?.toGenericProduct()

  const formatPrice = (price: number, currency: string = 'USD'): string => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price)
    } catch {
      return `$${price.toFixed(2)}`
    }
  }

  const handlePurchase = async () => {
    if (!strategy) return

    const trackingData = strategy.getTrackingData()
    setIsLoading(true)
    setError(null)

    try {
      eventTracker(TRACKING_TYPES.PAYMENT_STARTED, {
        ...trackingData,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
      })

      const result = await strategy.purchase()

      if (typeof result === 'string') {
        setStripeClientSecret(result)
        return
      }

      eventTracker(TRACKING_TYPES.PAYMENT_COMPLETED_SUCCESS, {
        ...trackingData,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
      })
      trackPurchaseCompleted({
        purchase_flow: 'redeem',
        product_id: trackingData.product_id,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
      })
    } catch (err) {
      console.error('Purchase failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Purchase failed'
      setError(errorMessage)

      eventTracker(TRACKING_TYPES.PAYMENT_FAILED, {
        ...trackingData,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
        error: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (stripeClientSecret) {
    return (
      <CheckoutShell onBack={() => setStripeClientSecret(null)}>
        <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret: stripeClientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </CheckoutShell>
    )
  }

  if (!product) return null

  const benefits = promotionConfig?.benefits ?? []
  const priceDisplay = formatPrice(product.price, product.currency)
  const faqItems: AppDisclosureItem[] = (variant === 'extension' ? EXTENSION_FAQ_ITEMS : BASE_FAQ_ITEMS).map(
    (item) => ({ question: item.question, answer: item.answer })
  )

  const renderContent = () => {
    if (error) {
      return (
        <div className="flex flex-col items-start" role="alert">
          <div className="w-12 h-12 rounded-full bg-[color:var(--negative)]/10 flex items-center justify-center mb-6">
            <svg
              aria-hidden="true"
              className="w-6 h-6 text-[color:var(--negative)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-3">
            Something went wrong
          </h1>
          <p className="text-base leading-relaxed text-[color:var(--ink-soft)] mb-8 max-w-[55ch]">{error}</p>
          <AppButton
            onClick={() => {
              setError(null)
              setStripeClientSecret(null)
            }}
          >
            Try Again
          </AppButton>
        </div>
      )
    }

    return (
      <>
        <h1 className="text-3xl md:text-4xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
          {product.displayName}
        </h1>

        <p className="mt-3 text-2xl font-semibold text-[color:var(--ink)]">
          {priceDisplay}
          <span className="text-base font-normal text-[color:var(--ink-soft)] ml-2">One-time payment</span>
        </p>

        <p className="mt-6 text-base leading-relaxed text-[color:var(--ink-soft)] max-w-[55ch]">
          {promotionConfig?.instructions ||
            'Prove your real level on the court. Play 8+ matches during the Reset window, get a fresh rating, and keep whichever one is higher.'}
        </p>

        {benefits.length > 0 && (
          <ul className="mt-8 space-y-3">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-3">
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 text-[color:var(--positive)] flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-[color:var(--ink-soft)]">{benefit}</span>
              </li>
            ))}
          </ul>
        )}

        <AppButton fullWidth className="mt-10" onClick={handlePurchase} loading={isLoading}>
          {isLoading ? 'Processing...' : 'Reset My Rating'}
        </AppButton>

        <p className="mt-6 text-xs text-[color:var(--ink-soft)]">
          One-time payment of {priceDisplay}. By purchasing you agree to our{' '}
          <a
            href="https://www.dupr.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[color:var(--ink-soft)] underline hover:text-[color:var(--ink)] transition-colors"
          >
            Terms
          </a>{' '}
          and{' '}
          <a
            href="https://www.dupr.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[color:var(--ink-soft)] underline hover:text-[color:var(--ink)] transition-colors"
          >
            Privacy Policy
          </a>
          .
        </p>

        <div className="border-t border-[color:var(--border)] mt-12 pt-10">
          <h2 className="text-lg font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-2">
            Frequently Asked Questions
          </h2>

          <AppDisclosure items={faqItems} />
        </div>
      </>
    )
  }

  return (
    <AppShell>
      <div className="flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-[55%] sticky top-16 h-[calc(100vh-64px)] items-start justify-center p-8 lg:p-12">
          <img
            src="/img/reset-hero.png"
            alt="DUPR Rating Reset"
            className="w-full max-h-[calc(100vh-64px-96px)] object-cover rounded-[var(--radius-card)]"
          />
        </div>

        <div className="w-full lg:w-[45%]">
          <div className="mx-auto max-w-lg px-6 py-10 md:px-10 md:py-12 lg:px-16 lg:py-16">{renderContent()}</div>
        </div>
      </div>
    </AppShell>
  )
}

export default ResetProductPage
